<template>
  <el-form
    v-if="campaign.stream"
    ref="form"
    :model="campaign"
    label-position="top"
    :class="['campaign-form', { desktop: isDesktop }]"
    @submit.prevent
  >
    <el-form-item
      prop="name"
      :error="errors.name"
      :rules="{
        required: true,
        message: 'Please enter campaign name',
        trigger: ['blur', 'change']
      }"
      label="Name"
    >
      <el-input v-model="campaign.name" type="text" />
    </el-form-item>
    <el-form-item label="Stream">
      <StreamSelector
        v-model="campaign.stream"
        :disabled="isPersisted"
        :allowed-streams="allowedStreams"
        @update:modelValue="clearSpots"
      />
    </el-form-item>
    <el-form-item label="Ad type">
      <AdTypeSelect
        v-model="campaign.type_id"
        :disabled="isPersisted"
        :radio-view="true"
        :allowed-types="allowedTypes"
        @update:modelValue="clearSpots"
      />
    </el-form-item>
    <el-form-item
      v-if="campaign.type_id === AdType.Banner"
      :error="errors.size_id"
      label="Size"
    >
      <SizeSelect
        v-model="campaign.size_id"
        :disabled="isPersisted"
        :include-adaptive="isStaff || isPersisted"
      />
    </el-form-item>

    <CampaignTargeting v-model="campaign" @update="updateTargeting" />
    <el-form-item>
      <el-button
        type="primary"
        plain
        :disabled="loading"
        :icon="loading ? 'LoadingElementIcon' : ''"
        @click.prevent="onSubmit"
      >
        {{ actionText }}
      </el-button>
      <el-button plain :disabled="loading" @click="onCancel">
        <span>Cancel</span>
      </el-button>
    </el-form-item>
  </el-form>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue'
import AdTypeSelect from '@/components/Select/AdTypeSelect.vue'
import StreamSelector from '@/apps/traforama/components/StreamSelector.vue'
import CampaignTargeting from './CampaignTargeting.vue'
import SizeSelect from '@/components/Select/SizeSelect.vue'
import { CampaignValidationErrorsKey, AdType, RonType } from '@/api/types'
import useValidation from '@/hooks/useValidation'
import useResize from '@/hooks/useResize'
import { useUserStore } from '@/store/modules/user'
import useRouter from '@/hooks/useRouter'
import { scrollToElement } from '@/utils/scroll-to'

export default defineComponent({
  name: 'CampaignForm',
  components: {
    AdTypeSelect,
    StreamSelector,
    CampaignTargeting,
    SizeSelect
  },
  props: {
    modelValue: {
      required: true,
      type: Object
    },
    loading: {
      type: Boolean,
      default: false
    },
    actionText: {
      type: String,
      default: 'Save'
    },
    mediaKit: {
      type: Object,
      default: null
    }
  },
  setup(props, context) {
    const userStore = useUserStore()
    const { router } = useRouter()

    const { isStaff } = userStore
    const { validationErrors } = useValidation(CampaignValidationErrorsKey)
    const errors = validationErrors()
    const campaign = ref({ ...props.modelValue })
    const isPersisted = computed(() => {
      return campaign.value.id !== undefined
    })

    const { isDesktop } = useResize()
    const form = ref(null)
    const onSubmit = () => {
      // @ts-ignore: Object is possibly 'null'.
      form.value.validate((valid: boolean) => {
        if (valid) {
          context.emit('submit', campaign.value)
        } else {
          scrollToElement('.el-form-item.is-error')
        }
      })
    }

    const updateTargeting = async (val: any) => {
      campaign.value = { ...campaign.value, ...val }
      await context.emit('update:modelValue', campaign.value)
    }

    const clearSpots = () => {
      if (!isPersisted.value) {
        campaign.value.spot_ids = []
        campaign.value.spots_list_ids = []
        context.emit('update:modelValue', campaign.value)
      }
    }

    const allowedStreams = computed(() => {
      return props.mediaKit ? props.mediaKit.streams : []
    })
    const allowedTypes = computed(() => {
      return props.mediaKit ? props.mediaKit.types : []
    })

    const onCancel = () => {
      if (userStore.isProcessingOnboarding) {
        userStore.clearOnboardProcessingFlag()
      }

      router.push('/campaigns')
    }

    watch(
      (_: any) => props.modelValue,
      (val: any) => {
        campaign.value = val
      }
    )

    return {
      onSubmit,
      isPersisted,
      errors,
      isDesktop,
      AdType,
      clearSpots,
      campaign,
      updateTargeting,
      isStaff,
      allowedStreams,
      allowedTypes,
      onCancel,
      form
    }
  }
})
</script>

<style lang="scss" scoped>
.campaign-form {
  :deep(.el-form-item) {
    margin-bottom: 10px;

    .el-form-item__label {
      padding: 0;
    }
  }

  :deep(.el-collapse) {
    width: 100%;
  }

  &.desktop {
    > :deep(.el-form-item) {
      > .el-form-item__content {
        > .el-input,
        > .el-select {
          width: 33%;
          max-width: 33%;
        }
      }
    }

    :deep(.isp-editor__input) {
      width: 100%;
      max-width: 100%;
    }

    :deep(.full-width-control .el-select) {
      max-width: 100%;
      width: 100%;
    }
  }
}
:deep(.el-form-item) {
  scroll-margin-top: 150px;
}
</style>
